/**
 * Geocoding utilities using OpenStreetMap Nominatim API (free, no API key required)
 * For production, consider using Google Maps Geocoding API or Mapbox for better results
 */

interface GeocodeResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
  importance?: number;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

interface ReverseGeocodeResult {
  address: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
  display_name: string;
  lat: string;
  lon: string;
}

/**
 * Geocode an address to coordinates
 */
export const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number; address: string } | null> => {
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1&countrycodes=bw&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Stashit-Dash-App/1.0' // Required by Nominatim
        }
      }
    );

    if (!response.ok) {
      throw new Error('Geocoding request failed');
    }

    const data: GeocodeResult[] = await response.json();

    if (data.length === 0) {
      return null;
    }

    const result = data[0];
    return {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      address: result.display_name
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

/**
 * Reverse geocode coordinates to an address
 */
export const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Stashit-Dash-App/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Reverse geocoding request failed');
    }

    const data: ReverseGeocodeResult = await response.json();
    return data.display_name || null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

/**
 * Get address suggestions for autocomplete with improved search
 */
export const getAddressSuggestions = async (query: string, userLocation?: { lat: number; lng: number }): Promise<Array<{ address: string; lat: number; lng: number }>> => {
  try {
    if (query.length < 2) {
      return [];
    }

    const encodedQuery = encodeURIComponent(query);

    // Build search parameters with better accuracy
    let searchUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=8&addressdetails=1&dedupe=1`;

    // Add Botswana focus but allow broader search if no results
    searchUrl += `&countrycodes=bw`;

    // Add viewbox to prioritize Botswana area
    // Botswana coordinates: roughly -17.5 to -27.5 lat, 19.5 to 29.5 lng
    searchUrl += `&viewbox=19.5,-17.5,29.5,-27.5&bounded=0`;

    // Add user location bias if available
    if (userLocation) {
      searchUrl += `&lat=${userLocation.lat}&lon=${userLocation.lng}`;
    }

    const response = await fetch(searchUrl, {
      headers: {
        'User-Agent': 'Stashit-Dash-App/1.0'
      }
    });

    if (!response.ok) {
      throw new Error('Address suggestions request failed');
    }

    let data: GeocodeResult[] = await response.json();

    // If no results in Botswana, try a broader search
    if (data.length === 0 && query.length >= 3) {
      const broadResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&limit=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'Stashit-Dash-App/1.0'
          }
        }
      );

      if (broadResponse.ok) {
        data = await broadResponse.json();
      }
    }

    // Filter and prioritize results
    const suggestions = data
      .filter(item => {
        // Filter out very low importance results
        const importance = parseFloat(item?.importance?.toString() || '0');
        return importance > 0.1 || item.display_name.toLowerCase().includes(query.toLowerCase());
      })
      .map(item => ({
        address: item.display_name,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon)
      }))
      .slice(0, 6); // Limit to 6 results

    return suggestions;
  } catch (error) {
    console.error('Address suggestions error:', error);
    return [];
  }
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Get user's current location using Geolocation API
 */
export const getCurrentLocation = (): Promise<{ lat: number; lng: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }

        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Accept cached position up to 5 minutes old
      }
    );
  });
};

/**
 * Get address suggestions with current location bias
 */
export const getAddressSuggestionsWithLocation = async (query: string): Promise<Array<{ address: string; lat: number; lng: number }>> => {
  try {
    // Try to get user's current location for better search results
    const userLocation = await getCurrentLocation().catch(() => null);

    return getAddressSuggestions(query, userLocation || undefined);
  } catch (error) {
    // Fallback to regular suggestions if location access fails
    console.warn('Could not get user location, using regular search:', error);
    return getAddressSuggestions(query);
  }
};
